import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, CheckCircle2, FileCheck, FileText, Send } from 'lucide-react'
import { Page } from '@/components/Page'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/Modal'
import { FormField } from '@/components/FormField'
import { Toolbar } from '@/components/Toolbar'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { useBill, usePayBill, usePostBill, useSubmitBill } from '@/features/ap/queries'
import { apApi } from '@/features/ap/api'
import type { BillStatus } from '@/features/ap/types'
import { formatDate, formatDateTime } from '@/lib/locale'
import { formatInvoiceMoney } from '@/features/invoicing/format'

const STATUS_VARIANT: Record<BillStatus, 'default' | 'secondary' | 'danger' | 'success' | 'warning'> = {
  draft: 'secondary', pending_approval: 'warning', approved: 'default', scheduled: 'default',
  partially_paid: 'default', paid: 'success', overdue: 'danger', void: 'secondary', rejected: 'danger',
}

function fmt(v: string | number, cur: string) {
  const n = typeof v === 'number' ? v : parseFloat(v || '0')
  return formatInvoiceMoney(n, cur)
}

export function BillDetailPage() {
  const { id } = useParams<{ id: string }>()
  const billId = parseInt(id as string, 10)
  const navigate = useNavigate()
  const { toast } = useToast()
  const { hasPermission } = useAuth()
  const { data, isLoading } = useBill(billId)
  const submitMut = useSubmitBill()
  const postMut = usePostBill()
  const payMut = usePayBill()

  const [payOpen, setPayOpen] = useState(false)
  const [payAmount, setPayAmount] = useState('')
  const [payMethod, setPayMethod] = useState('manual')
  const [payRef, setPayRef] = useState('')
  const [paySendEmail, setPaySendEmail] = useState(true)

  if (isLoading) {
    return <Page title="Bill"><Card><div className="flex justify-center py-20"><LoadingSpinner /></div></Card></Page>
  }
  if (!data?.bill) {
    return <Page title="Bill not found"><Card><div className="p-8 text-center text-muted-foreground">Bill not found.</div></Card></Page>
  }
  const bill = data.bill
  const outstanding = +(Number(bill.total) - Number(bill.amount_paid || 0)).toFixed(2)
  // Why: a bill the user can post to the GL right now is one that's
  // already approved (manual posting mode left it that way) but never
  // hit the ledger. In auto mode `submitForApproval` posts in the same
  // request, so `posted_at` is non-null straight away - and we never
  // surface the post button. The hasPermission gate is the same one
  // that gates Submit, since Submit + Post are two halves of the same
  // approve-then-post action when posting is set to manual.
  const canSubmit = hasPermission('invoicing.bills.approve') && bill.status === 'draft'
  const canPostToGL = hasPermission('invoicing.bills.approve')
    && bill.status === 'approved'
    && !bill.posted_at
  const canPay = hasPermission('invoicing.bills.pay') && ['approved', 'partially_paid', 'overdue'].includes(bill.status) && outstanding > 0.005

  async function onSubmit() {
    try {
      const res = await submitMut.mutateAsync(bill.id)
      if (res && 'pending_approval' in res && res.pending_approval) {
        toast({ title: 'Submitted for approval', description: res.message })
        navigate(`/approvals/instances/${res.approval_instance_id}`)
        return
      }
      // The /submit endpoint approves the bill straight away when no
      // approval policy matches. Whether it ALSO posted to the GL
      // depends on the tenant's AP posting mode - we read the returned
      // bill rather than guessing, so the toast tracks reality.
      const updated = 'bill' in res ? res.bill : null
      if (updated && updated.posted_at) {
        toast({ title: 'Bill approved + posted to GL' })
      } else {
        toast({
          title: 'Bill approved',
          description: 'Posting is set to manual for AP - click Post to GL to record the journal entry.',
        })
      }
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Submit failed', variant: 'destructive' })
    }
  }

  async function onPostToGL() {
    try {
      await postMut.mutateAsync(bill.id)
      toast({ title: 'Bill posted to GL' })
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Post failed', variant: 'destructive' })
    }
  }

  // Vendor must have an email for the remittance email to go out.
  // If no email on file, the toggle is forced off + disabled; the
  // backend also no-ops silently in that case but disabling in UI
  // avoids showing a "will send" state that's actually a no-op.
  const vendorHasEmail = !!(data.bill as { vendor_email?: string | null }).vendor_email

  function openPay() {
    setPayAmount(outstanding > 0 ? outstanding.toFixed(2) : '')
    setPayMethod('manual')
    setPayRef('')
    setPaySendEmail(vendorHasEmail)
    setPayOpen(true)
  }

  async function onPayConfirm() {
    const amt = Number(payAmount)
    if (!Number.isFinite(amt) || amt <= 0) {
      toast({ title: 'Enter a positive amount', variant: 'destructive' }); return
    }
    try {
      const res = await payMut.mutateAsync({
        id: bill.id,
        body: {
          amount: amt,
          method: payMethod,
          reference: payRef || undefined,
          send_email: vendorHasEmail && paySendEmail,
        },
      })
      toast({
        title: 'Payment recorded',
        description: res.email_sent ? 'Remittance email sent to the vendor.' : undefined,
      })
      setPayOpen(false)
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Payment failed', variant: 'destructive' })
    }
  }

  return (
    <Page
      title={bill.bill_number}
      description={`${bill.vendor_display_name} · ${fmt(bill.total, bill.currency)}`}
      actions={
        <Toolbar align="end">
          <Button variant="ghost" asChild><Link to="/invoicing/bills"><ArrowLeft className="mr-1 size-4" /> Back</Link></Button>
          {canSubmit ? (
            <Button onClick={onSubmit} disabled={submitMut.isPending}>
              <FileCheck className="mr-1 size-4" /> Submit for approval
            </Button>
          ) : null}
          {canPostToGL ? (
            <Button onClick={onPostToGL} disabled={postMut.isPending}>
              <FileCheck className="mr-1 size-4" /> Post to GL
            </Button>
          ) : null}
          {bill.approval_instance_id ? (
            <Button variant="secondary" asChild>
              <Link to={`/approvals/instances/${bill.approval_instance_id}`}><Send className="mr-1 size-4" /> View approval</Link>
            </Button>
          ) : null}
          {bill.attachment_s3_key ? (
            <Button variant="secondary" onClick={async () => {
              try {
                const { url } = await apApi.attachmentUrl(bill.id)
                window.open(url, '_blank', 'noopener,noreferrer')
              } catch (err) {
                toast({ title: err instanceof Error ? err.message : 'Failed to open attachment', variant: 'destructive' })
              }
            }}>
              <FileText className="mr-1 size-4" /> View attachment
            </Button>
          ) : null}
          {canPay ? (
            <Button variant="secondary" onClick={openPay}>
              <CheckCircle2 className="mr-1 size-4" /> Record payment
            </Button>
          ) : null}
        </Toolbar>
      }
    >
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <div className="space-y-4 p-4">
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant={STATUS_VARIANT[bill.status]}>{bill.status.replace(/_/g, ' ')}</Badge>
              <span className="text-sm text-muted-foreground">
                Issue {formatDate(bill.issue_date)} → Due {formatDate(bill.due_date)}
              </span>
              {bill.vendor_invoice_number ? (
                <span className="text-sm text-muted-foreground">Vendor inv: <span className="text-foreground">{bill.vendor_invoice_number}</span></span>
              ) : null}
            </div>
            <div>
              <div className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Line items</div>
              <div className="divide-y rounded-md border text-sm">
                <div className="grid grid-cols-12 gap-2 bg-muted/40 px-3 py-2 text-xs uppercase text-muted-foreground">
                  <div className="col-span-6">Description</div>
                  <div className="col-span-2 text-right">Qty</div>
                  <div className="col-span-2 text-right">Unit</div>
                  <div className="col-span-2 text-right">Amount</div>
                </div>
                {bill.lines.map(l => (
                  <div key={l.id} className="grid grid-cols-12 gap-2 px-3 py-2">
                    <div className="col-span-6">
                      <div>{l.description}</div>
                      <div className="text-xs text-muted-foreground">{l.account_code}</div>
                    </div>
                    <div className="col-span-2 text-right">{Number(l.quantity)}</div>
                    <div className="col-span-2 text-right">{fmt(l.unit_price, bill.currency)}</div>
                    <div className="col-span-2 text-right">{fmt(l.amount, bill.currency)}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex flex-col items-end gap-1 text-sm">
                <div>Subtotal: {fmt(bill.subtotal, bill.currency)}</div>
                {Number(bill.discount) > 0 ? (
                  <div className="text-muted-foreground">
                    Discount: −{fmt(bill.discount, bill.currency)}
                  </div>
                ) : null}
                {Number(bill.shipping_amount) > 0 ? (
                  <div>Shipping: {fmt(bill.shipping_amount, bill.currency)}</div>
                ) : null}
                {Number(bill.tax_total) > 0 ? (
                  <div>
                    {bill.header_tax_rate_pct
                      ? `Tax (${Number(bill.header_tax_rate_pct)}%)`
                      : 'Tax'}
                    : {fmt(bill.tax_total, bill.currency)}
                  </div>
                ) : null}
                <div className="text-base font-bold">Total: {fmt(bill.total, bill.currency)}</div>
                {Number(bill.amount_paid) > 0 ? <div className="text-muted-foreground">Paid: {fmt(bill.amount_paid, bill.currency)}</div> : null}
                {outstanding > 0 ? <div className="text-muted-foreground">Outstanding: {fmt(outstanding, bill.currency)}</div> : null}
              </div>
            </div>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <div className="mb-3 text-sm font-semibold">Activity</div>
            <ol className="space-y-3 text-sm">
              {bill.status_history.map(h => (
                <li key={h.id} className="flex gap-3">
                  <span className="mt-1.5 size-2 shrink-0 rounded-full bg-primary" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{h.from_status ? `${h.from_status} → ${h.to_status}` : `Created as ${h.to_status}`}</div>
                    <div className="text-xs text-muted-foreground">{formatDateTime(h.created_at)}{h.actor_email ? ` · ${h.actor_email}` : ''}</div>
                    {h.reason ? <div className="text-xs text-muted-foreground">{h.reason}</div> : null}
                  </div>
                </li>
              ))}
            </ol>
            {bill.payments.length > 0 ? (
              <>
                <hr className="my-4" />
                <div className="mb-2 text-sm font-semibold">Payments</div>
                <ul className="space-y-2 text-sm">
                  {bill.payments.map(p => (
                    <li key={p.id} className="flex justify-between">
                      <span>{fmt(p.amount, p.currency)}<span className="ml-2 text-xs text-muted-foreground">{p.method}</span></span>
                      <span className="text-xs text-muted-foreground">{formatDateTime(p.paid_at)}</span>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </div>
        </Card>
      </div>

      <Modal.Confirm
        open={payOpen}
        onOpenChange={setPayOpen}
        title={`Record payment for ${bill.bill_number}`}
        body={
          <div className="space-y-3">
            <p>Outstanding balance: <strong>{fmt(outstanding, bill.currency)}</strong>. Posts Dr AP / Cr Cash to the ledger.</p>
            <FormField label={`Amount (${bill.currency})`} type="number" step="0.01" min={0} value={payAmount} onChange={e => setPayAmount(e.currentTarget.value)} />
            <FormField label="Method" value={payMethod} onChange={e => setPayMethod(e.currentTarget.value)} hint="ach, wire, check, card, manual…" />
            <FormField label="Reference" value={payRef} onChange={e => setPayRef(e.currentTarget.value)} hint="Bank reference or check number" />
            <label className="flex items-center gap-2 text-sm">
              {/* eslint-disable-next-line no-restricted-syntax -- checkbox primitive not in shadcn wrapper set */}
              <input
                type="checkbox"
                className="size-4 rounded border-input"
                checked={vendorHasEmail && paySendEmail}
                onChange={e => setPaySendEmail(e.currentTarget.checked)}
                disabled={!vendorHasEmail}
              />
              <span>
                Send remittance email to vendor
                {!vendorHasEmail ? (
                  <span className="ml-2 text-xs text-muted-foreground">(vendor has no email on file)</span>
                ) : null}
              </span>
            </label>
          </div>
        }
        confirmLabel="Record payment"
        confirmVariant="primary"
        busy={payMut.isPending}
        onConfirm={onPayConfirm}
      />
    </Page>
  )
}
